import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandMark } from "../design/BrandMark";

import { requests, send } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import { homePathFor } from "../session/access";
import { useSession } from "../session/SessionProvider";
import { offersEventClinicSignIn } from "../api/base";
import { CLERK_PUBLISHABLE_KEY, eventClinicToken, mountOwnSignIn, RETURN_MARKER } from "../system/eventClinicSignIn";
import { SURFACE_PREFIX } from "../system/navigation";
import { Challenge, type ChallengeHandle, turnstileSiteKey } from "../system/Turnstile";

// ---------------------------------------------------------------------------
// Sign in.
//
// THE SCREEN SHOWS WHATEVER THE SERVER SAYS AND NEVER ELABORATES.
//
// V3 spent real effort making the API's answers indistinguishable: an unknown
// address, a wrong password and a locked account produce byte-identical
// responses and take the same time. A helpful client that added "no account
// with that address" would hand back the account-enumeration oracle the server
// went to that trouble to withhold. So the message rendered here is the one the
// server sent, and nothing is inferred from a status code.
//
// The account-recovery actions post and then say the same thing whatever
// happened, for the same reason.
// ---------------------------------------------------------------------------

type Outcome = { tone: "failed" | "attention" | "live"; status: string; message: string } | null;

/** S63. Nobody is known yet, so the browser's language (translate.ts, browserLocale). */
export function SignIn() {
  return (
    <PreSessionLocale>
      <SignInForm />
    </PreSessionLocale>
  );
}

function SignInForm() {
  const { t, refusal, locale } = useT();
  const { apiBase, reload } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        requests.login(apiBase, email, password),
      );
      if (status !== 200) {
        setOutcome({
          tone: "failed",
          status: t("Not signed in"),
          message: refusal(body.error) ?? t("That did not work. Please try again."),
        });
        return;
      }
      // The session is new, so everything derived from it is stale. Re-ask
      // rather than merging the login response into local state: /v1/me is the
      // one shape the rest of the application reads, and a second way of
      // producing it is a second thing that can be wrong.
      const me = await reload();
      navigate((me && homePathFor(me)) ?? SURFACE_PREFIX.console, { replace: true });
    } catch {
      setOutcome({
        tone: "attention",
        status: t("No connection"),
        message: t("We could not reach Verbunk. Check your connection and try again."),
      });
    } finally {
      setBusy(false);
    }
  }

  // S0-163. On verbunk.event.clinic only: the shared Clerk, exchanged for this console's own
  // session. XP-5: nobody signed in yet gets Clerk's sign-in drawn on THIS page, under
  // Verbunk's name; signed in, it returns here with the marker and finishes by itself.
  const eventClinic = offersEventClinicSignIn(window.location.origin) && CLERK_PUBLISHABLE_KEY !== "";
  const resumed = useRef(false);
  const clerkMount = useRef<HTMLDivElement>(null);
  async function signInWithEventClinic() {
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      const back = new URL(window.location.href);
      back.searchParams.set(RETURN_MARKER, "1");
      const token = await eventClinicToken(locale);
      if (token === null) {
        if (clerkMount.current) await mountOwnSignIn(clerkMount.current, locale, back.toString());
        return;
      }
      const { status, body } = await send<{ error?: { message?: string } }>(requests.clerkExchange(apiBase, token));
      if (status !== 200) {
        setOutcome({ tone: "failed", status: t("Not signed in"), message: refusal(body.error) ?? t("That did not work. Please try again.") });
        return;
      }
      const me = await reload();
      navigate((me && homePathFor(me)) ?? SURFACE_PREFIX.console, { replace: true });
    } catch {
      setOutcome({ tone: "attention", status: t("No connection"), message: t("event.clinic's sign-in could not be reached. Try again.") });
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    if (!eventClinic || resumed.current) return;
    if (new URL(window.location.href).searchParams.get(RETURN_MARKER) !== "1") return;
    resumed.current = true;
    void signInWithEventClinic();
  }, [eventClinic]);

  const [providers, setProviders] = useState<Array<{ key: string; label: string }>>([]);
  const [challengeShown, setChallengeShown] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const challenge = useRef<ChallengeHandle | null>(null);

  // ★ THE CHALLENGE APPEARS ONLY AFTER "Forgotten password" IS PRESSED, and
  // never for the sign-in form or the magic link. §5 keeps Turnstile off login
  // "where it punishes legitimate users on event day", and BOTH live on this
  // screen — so a widget rendered here unconditionally would challenge exactly
  // the person §5 protects. A magic link is a LOGIN mechanism, which is why it
  // is not challenged either, even though it mails a bearer link like a reset
  // does. Widening that is a ruling to change in `07-authentication.md`, not a
  // line to add here.
  // NOTHING RENDERS UNTIL THE SERVER AGREES. An empty list is the ordinary
  // state of a deployment with no provider credentials, and it draws nothing
  // rather than a button that cannot work.
  useEffect(() => {
    void (async () => {
      const res = await send<{ providers?: Array<{ key: string; label: string }> }>(
        requests.oidcProviders(apiBase),
      );
      setProviders(res.status === 200 && Array.isArray(res.body?.providers) ? res.body.providers : []);
    })();
  }, [apiBase]);

  // ★ THE BROWSER LEAVES THIS PAGE, so the provider is remembered where a
  // redirect cannot lose it. sessionStorage rather than a variable: the callback
  // is a fresh document. It holds a provider KEY and never a secret.
  async function startOidc(providerKey: string) {
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      const res = await send<{ authorization_url?: string; error?: { message: string } }>(
        requests.oidcStart(apiBase, providerKey),
      );
      if (res.status === 200 && typeof res.body?.authorization_url === "string") {
        window.sessionStorage.setItem("oidc.provider", providerKey);
        window.location.assign(res.body.authorization_url);
        return;
      }
      setOutcome({
        tone: "failed",
        status: t("Not available"),
        message: refusal(res.body?.error) ?? t("That sign-in method is not available."),
      });
    } catch {
      setOutcome({
        tone: "attention",
        status: t("No connection"),
        message: t("We could not reach Verbunk. Check your connection and try again."),
      });
    } finally {
      setBusy(false);
    }
  }

  async function recover(kind: "reset" | "link" | "verify") {
    if (busy) return;
    if (kind === "reset" && turnstileSiteKey() && challengeToken === null) {
      setChallengeShown(true);
      return;
    }
    await sendRecovery(kind, challengeToken ?? undefined);
  }

  async function sendRecovery(kind: "reset" | "link" | "verify", token?: string) {
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      await send(
        kind === "reset"
          ? requests.requestPasswordReset(apiBase, email, token)
          : kind === "link"
            ? requests.requestMagicLink(apiBase, email)
            : requests.requestEmailVerification(apiBase, email),
      );
    } catch {
      // Deliberately swallowed. The acknowledgement below must not depend on
      // what happened, and a network error is one more thing that could be read
      // as "that address exists".
    } finally {
      setBusy(false);
      if (kind === "reset") {
        // ★ A TURNSTILE TOKEN IS SPENT ONCE siteverify HAS SEEN IT, and the
        // acknowledgement below is deliberately IDENTICAL whatever happened —
        // that is this screen's anti-enumeration design and it is correct. The
        // two together are the hazard: a second press carrying the used token
        // would be refused by the API and the person would still be told a link
        // was on its way. So the challenge is torn down and re-asked instead.
        setChallengeToken(null);
        setChallengeShown(false);
        challenge.current?.reset();
      }
      setOutcome({
        tone: "live",
        status: t("Sent"),
        message:
          kind === "reset"
            ? t("If that address has an account, a link to set a new password is on its way.")
            : kind === "link"
              ? t("If that address has an account, a sign-in link is on its way.")
              : t("If that address has an account, a link to confirm it is on its way."),
      });
    }
  }

  return (
    <main className="centre-page">
      <BrandMark />
      <div className="centre-card">
        <h1>{t("Sign in")}</h1>

        <div className="form-message" role="status" aria-live="polite">
          {outcome ? (
            <>
              <StatusChip tone={outcome.tone} label={outcome.status} />
              <span>{outcome.message}</span>
            </>
          ) : null}
        </div>

        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="signin-email">{t("Email address")}</label>
            <input
              id="signin-email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="signin-password">{t("Password")}</label>
            <input
              id="signin-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button className="btn btn-block" type="submit" disabled={busy}>
            {busy ? t("Working…") : t("Sign in")}
          </button>
        </form>

        {eventClinic ? (
          <button className="btn btn-block" type="button" onClick={() => void signInWithEventClinic()} disabled={busy}>
            {t("Sign in with event.clinic")}
          </button>
        ) : null}
        {eventClinic ? <div ref={clerkMount} /> : null}

        {providers.length > 0 ? (
          <div className="stack">
            {providers.map((p) => (
              <button
                key={p.key}
                className="btn btn-secondary btn-block"
                type="button"
                onClick={() => void startOidc(p.key)}
                disabled={busy}
              >
                {t("Continue with {provider}", { provider: p.label })}
              </button>
            ))}
          </div>
        ) : null}

        <div className="row">
          <button className="btn btn-quiet" type="button" onClick={() => void recover("link")} disabled={busy}>
            {t("Email me a sign-in link")}
          </button>
          <button className="btn btn-quiet" type="button" onClick={() => void recover("reset")} disabled={busy}>
            {t("Forgotten password")}
          </button>
          {/* Sweep S21a. Here because the address is typed here, and because
              asking for it must answer the same constant as the two above. */}
          <button className="btn btn-quiet" type="button" onClick={() => void recover("verify")} disabled={busy}>
            {t("Send the confirmation email again")}
          </button>
        </div>

        {challengeShown ? (
          <div className="row">
            <Challenge
              handle={challenge}
              onToken={(token) => {
                setChallengeToken(token);
                if (token !== null) void sendRecovery("reset", token);
              }}
            />
          </div>
        ) : null}
      </div>
    </main>
  );
}
